import type { FullFranchiseAccessFragment, FullSalonAccessCoreFragment } from "@/gql/graphql";
import type { SalonDisplayData, SalonGroup, SalonWithFranchise } from "./types";

export function isLocationInName(name: string, city?: string): boolean {
  if (!city) return false;
  const nameLower = name.toLowerCase();
  const cityLower = city.toLowerCase();
  return nameLower.includes(cityLower);
}

export function getSalonDisplayText(
  salon: FullSalonAccessCoreFragment | SalonWithFranchise,
  franchiseHasMultipleSalons: boolean | null = false
): SalonDisplayData {
  // If franchise has multiple salons, show franchise on first line, salon on second line
  if (franchiseHasMultipleSalons) {
    const salonWithFranchise = salon as SalonWithFranchise;
    const shouldShowLocation = !isLocationInName(salon.name, salon.city ?? undefined);
    return {
      isMultiLine: true,
      franchiseName: salonWithFranchise.franchise.name,
      salonName: salon.name,
      location: shouldShowLocation
        ? salon.city && salon.state
          ? `${salon.city}, ${salon.state}`
          : salon.address
        : undefined,
    };
  }

  // Single line format for standalone salons or single-salon franchises
  const parts = [salon.name];

  // Only add location if it's not already in the salon name
  if (!isLocationInName(salon.name, salon.city ?? undefined)) {
    if (salon.city && salon.state) {
      parts.push(`${salon.city}, ${salon.state}`);
    } else if (salon.address) {
      parts.push(salon.address);
    }
  }

  return {
    isMultiLine: false,
    text: parts.join(" • "),
  };
}

export function shouldGroupByFranchise(groups: SalonGroup[]): boolean {
  // Group by franchise if there are multiple franchises or a franchise has multiple salons
  const franchiseGroups = groups.filter((g) => g.franchise);
  const hasMultipleFranchises = franchiseGroups.length > 1;
  const hasFranchiseWithMultipleSalons = franchiseGroups.some((g) => g.salons.length > 1);

  return hasMultipleFranchises || hasFranchiseWithMultipleSalons;
}

/**
 * Flatten franchise access data into salons with embedded franchise information
 */
export function flattenSalonsWithFranchise(
  franchiseAccess: FullFranchiseAccessFragment[]
): SalonWithFranchise[] {
  return franchiseAccess.flatMap((fa) =>
    fa.franchise.salons.map((salon) => ({
      ...salon,
      franchise: fa.franchise,
    }))
  );
}
